/**
 * Pure translation between the quota grid's row shape and the `userQuotas` API.
 *
 * The grid was built against localStorage and thinks in string period keys
 * ("2026-03", "2026-Q1", "2026"). The backend (coffee #4146) thinks in
 * `(periodType, periodStart)` pairs. Everything that converts between the two
 * lives here, with no React and no Apollo, so the round-trip can be tested
 * exhaustively — a wrong period key silently reads someone else's quarter.
 *
 * `QuotaRow` and `QuotaPeriod` live here rather than in `QuotasContext` because
 * the context now imports this module; the context re-exports both so existing
 * importers are unaffected.
 */

import { QuotaPeriodType, type UserQuotaInput } from "@/lib/graphql/types-and-hooks";

/**
 * Period granularity for a quota row.
 * - "monthly":  periodKey = "YYYY-MM"          (e.g. "2026-03")
 * - "quarterly": periodKey = "YYYY-Qn"         (e.g. "2026-Q1")
 * - "annual":   periodKey = "YYYY"             (e.g. "2026")
 */
export type QuotaPeriod = "monthly" | "quarterly" | "annual";

export interface QuotaRow {
  /** Workspace member id this quota belongs to. */
  userId: string;
  period: QuotaPeriod;
  periodKey: string;
  /** Quota amount in the workspace currency. */
  amount: number;
  /**
   * Legacy flag from the localStorage era. Nothing reads it — `getEffectiveQuota`
   * decides rollup purely from which rows exist — so it is deliberately NOT
   * persisted to the backend, which has no column for it.
   */
  managerOverride?: boolean;
}

export const PERIOD_TO_API: Record<QuotaPeriod, QuotaPeriodType> = {
  monthly: QuotaPeriodType.Month,
  quarterly: QuotaPeriodType.Quarter,
  annual: QuotaPeriodType.Year,
};

const API_TO_PERIOD: Record<string, QuotaPeriod> = {
  month: "monthly",
  quarter: "quarterly",
  year: "annual",
  // Reads and writes disagree on case, on purpose. `UserQuotaInput.periodType`
  // is the GraphQL enum, whose members are MONTH/QUARTER/YEAR; but
  // `UserQuotaType.periodType` is a plain String field reading the Postgres
  // column, which stores the lowercase Python values. Accept both so a change
  // on either side does not silently drop every row.
  MONTH: "monthly",
  QUARTER: "quarterly",
  YEAR: "annual",
};

/**
 * Period key → the ISO date the backend stores as `period_start`.
 *
 * Quarters and years both start in the first month of their period, matching
 * `normalize_period_start` server-side. Returns null for a malformed key rather
 * than guessing a date — a bad key must not become a write against January.
 */
export function periodKeyToPeriodStart(period: QuotaPeriod, periodKey: string): string | null {
  if (period === "annual") {
    return /^\d{4}$/.test(periodKey) ? `${periodKey}-01-01` : null;
  }
  if (period === "quarterly") {
    const m = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    if (!m) return null;
    const firstMonth = (Number(m[2]) - 1) * 3 + 1;
    return `${m[1]}-${String(firstMonth).padStart(2, "0")}-01`;
  }
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(periodKey);
  return m ? `${m[1]}-${m[2]}-01` : null;
}

/** The inverse: a stored `(periodType, periodStart)` back to a grid period key. */
export function periodStartToPeriodKey(
  periodType: string,
  periodStart: string,
): { period: QuotaPeriod; periodKey: string } | null {
  const period = API_TO_PERIOD[periodType];
  if (!period) return null;
  // The wire value is an ISO date; take the date part so a datetime is tolerated.
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(periodStart);
  if (!m) return null;
  const [, year, month] = m;
  if (period === "annual") return { period, periodKey: year };
  if (period === "quarterly") {
    const q = Math.floor((Number(month) - 1) / 3) + 1;
    return { period, periodKey: `${year}-Q${q}` };
  }
  return { period, periodKey: `${year}-${month}` };
}

/** Identity of a quota cell — one user, one period, one period key. */
export function quotaRowKey(row: Pick<QuotaRow, "userId" | "period" | "periodKey">): string {
  return `${row.userId}::${row.period}::${row.periodKey}`;
}

/** Server rows → grid rows. Rows with an unparseable period are dropped. */
export function quotaNodesToRows(
  nodes: ReadonlyArray<{
    userId?: number | null;
    periodType?: string | null;
    periodStart?: string | null;
    amount?: number | null;
  } | null> | null | undefined,
): QuotaRow[] {
  const rows: QuotaRow[] = [];
  for (const node of nodes ?? []) {
    if (!node || node.userId == null || !node.periodType || !node.periodStart) continue;
    // A null amount means no quota — the backend deletes rather than storing 0,
    // so this is belt-and-braces against a stale row.
    if (node.amount == null) continue;
    const parsed = periodStartToPeriodKey(node.periodType, node.periodStart);
    if (!parsed) continue;
    rows.push({
      userId: String(node.userId),
      period: parsed.period,
      periodKey: parsed.periodKey,
      amount: node.amount,
    });
  }
  return rows;
}

/**
 * The write payload for a whole-grid save: every cell that changed between two
 * row sets.
 *
 * Deletions round-trip as `amount: null`, which is how `setUserQuotas` removes a
 * row — there is no `deleteUserQuota` mutation. Omitting them instead would make
 * "clear this quota" a silent no-op, the same failure mode as the `updateNote`
 * due-date bug.
 *
 * Rows whose period key does not parse are skipped: they cannot address a
 * backend row, so sending them would be a write against the wrong period.
 */
export function diffQuotaRows(prev: QuotaRow[], next: QuotaRow[]): UserQuotaInput[] {
  const prevByKey = new Map(prev.map(r => [quotaRowKey(r), r]));
  const nextByKey = new Map(next.map(r => [quotaRowKey(r), r]));
  const out: UserQuotaInput[] = [];

  for (const [key, row] of nextByKey) {
    const before = prevByKey.get(key);
    if (before && before.amount === row.amount) continue;
    const periodStart = periodKeyToPeriodStart(row.period, row.periodKey);
    if (!periodStart) continue;
    out.push({
      userId: row.userId,
      periodType: PERIOD_TO_API[row.period],
      periodStart,
      amount: row.amount,
    });
  }

  for (const [key, row] of prevByKey) {
    if (nextByKey.has(key)) continue;
    const periodStart = periodKeyToPeriodStart(row.period, row.periodKey);
    if (!periodStart) continue;
    out.push({
      userId: row.userId,
      periodType: PERIOD_TO_API[row.period],
      periodStart,
      amount: null,
    });
  }

  return out;
}

/**
 * Inclusive `from`/`to` covering every period the grid can show for `years`.
 *
 * The grid stores monthly rows and derives quarters and years from them, so one
 * MONTH-typed query over the full span backs every column.
 */
export function quotaWindowForYears(years: number[]): { from: string; to: string } | null {
  if (years.length === 0) return null;
  const min = Math.min(...years);
  const max = Math.max(...years);
  return { from: `${min}-01-01`, to: `${max}-12-31` };
}
